// A bordered picker in a bar (canvas 4b "Desktop · None": the resolution, the network mode): the value and a
// chevron; a menu of choices under it, the current one checked.

import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { Ic, Pix, presence, usePresence } from './ds';

export type DropItem<V extends string> = { value: V; label: string };

export function Dropdown<V extends string>({
  value,
  shown,
  items,
  onChange,
  heading,
  label,
  title,
  testid,
}: {
  value: V;
  /** what the button says, when not the item's own label */
  shown?: string;
  items: DropItem<V>[];
  onChange: (v: V) => void;
  /** the menu's heading, in pixel caps */
  heading: string;
  /** the button's accessible name */
  label: string;
  title?: string;
  testid?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const p = usePresence(open, 120);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const current = items.find((i) => i.value === value);
  return (
    <span className="ws-menu-wrap" ref={ref}>
      <button
        type="button"
        className="ws-drop"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${shown ?? current?.label ?? value}`}
        title={title}
        data-testid={testid}
        data-value={value}
        onClick={() => setOpen((o) => !o)}
      >
        {shown ?? current?.label ?? value}
        <span className="ws-drop-chev">
          <Ic icon={IconChevronDown} size={14} />
        </span>
      </button>
      {p.mounted && (
        <div className="ws-menu ws-drop-menu ds-float" role="listbox" aria-label={label} {...presence(p)}>
          <div className="ws-menu-label">
            <Pix>{heading}</Pix>
          </div>
          {items.map((i) => (
            <button
              key={i.value}
              type="button"
              className="ws-menu-item"
              role="option"
              aria-selected={i.value === value}
              aria-checked={i.value === value}
              data-testid={testid ? `${testid}-${i.value}` : undefined}
              onClick={() => {
                setOpen(false);
                if (i.value !== value) onChange(i.value);
              }}
            >
              <span className="ws-menu-check">{i.value === value && <Ic icon={IconCheck} size={14} stroke={2} />}</span>
              {i.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
